import { normalizeVaultTenantId } from './vault-tenant-scope.util';

const VAULT_NEBULA_COMPANY_KEY = 'vault_nebula_company';

export interface VaultNebulaCompanyContext {
  companyId: string;
  companyName: string;
  tenantId?: string;
}

function unwrapAuthMeRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  const nested = root['data'];
  if (nested && typeof nested === 'object') {
    return nested as Record<string, unknown>;
  }
  return root;
}

export function readAuthMeCompanyId(me: unknown): string {
  const record = unwrapAuthMeRecord(me);
  if (!record) return readCompanyIdFromUserData();
  const context = record['context'];
  if (context && typeof context === 'object') {
    const ctx = context as Record<string, unknown>;
    const fromContext = ctx['company_id'] ?? ctx['companyId'];
    if (fromContext != null && String(fromContext).trim()) {
      return String(fromContext).trim();
    }
  }
  for (const key of ['company_id', 'companyId'] as const) {
    const value = record[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return readCompanyIdFromUserData();
}

export function readCompanyIdFromUserData(): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return '';
    const data = JSON.parse(raw) as Record<string, unknown>;
    const value = data['companyId'] ?? data['company_id'];
    return value != null ? String(value).trim() : '';
  } catch {
    return '';
  }
}

export function readCustomerIdFromUserData(): number | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return null;
    const data = JSON.parse(raw) as { customerId?: number };
    return data?.customerId ?? null;
  } catch {
    return null;
  }
}

/** Etiqueta Simappe (producto/cliente), no empresa Vault (`Egs`, `Red Summa`, …). */
export function isSimappeProductCompanyLabel(name: string | null | undefined): boolean {
  const label = (name ?? '').trim().toLowerCase();
  if (!label) return false;
  if (label === 'datavault' || label === 'datavault main') return true;
  if (label.startsWith('datavault -') || label.startsWith('datavault-')) return true;
  if (label.includes('datavault') && label.includes('main')) return true;
  return false;
}

export function extractTenantIdFromCompanyRecord(
  company: Record<string, unknown>,
): string {
  for (const key of [
    'tenant_id',
    'tenantId',
    'vault_tenant_id',
    'datavault_tenant_id',
    'uuid',
    'external_id',
    'externalId',
  ] as const) {
    const raw = company[key];
    if (raw == null) continue;
    const value = String(raw).trim();
    if (!value) continue;
    const normalized = normalizeVaultTenantId(value);
    if (normalized) return normalized;
  }
  return '';
}

export function filterDatavaultCompanies(companies: unknown[]): Record<string, unknown>[] {
  if (!Array.isArray(companies)) return [];
  return companies.filter((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const row = entry as Record<string, unknown>;
    const customer = row['customer'] as { firstName?: string } | undefined;
    return customer?.firstName === 'Datavault';
  }) as Record<string, unknown>[];
}

export function persistVaultNebulaCompanyContext(ctx: VaultNebulaCompanyContext): void {
  if (typeof sessionStorage === 'undefined') return;
  const companyId = (ctx.companyId || '').trim();
  const companyName = (ctx.companyName || '').trim();
  if (!companyId && !companyName) return;

  sessionStorage.setItem(
    VAULT_NEBULA_COMPANY_KEY,
    JSON.stringify({
      companyId,
      companyName,
      tenantId: (ctx.tenantId || '').trim() || undefined,
    }),
  );

  // Solo contexto Nebula/Simappe (login, jwt-select-company). No pisar empresa Vault
  // asignada en user_tenants (my-tenants / Gestión de Usuarios).
  const tenantId = normalizeVaultTenantId(ctx.tenantId);
  if (tenantId) {
    ctx.tenantId = tenantId;
  }
}

export function readVaultNebulaCompanyContext(): VaultNebulaCompanyContext | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(VAULT_NEBULA_COMPANY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VaultNebulaCompanyContext;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      companyId: String(parsed.companyId ?? '').trim(),
      companyName: String(parsed.companyName ?? '').trim(),
      tenantId: parsed.tenantId ? String(parsed.tenantId).trim() : undefined,
    };
  } catch {
    return null;
  }
}

export function enrichVaultCompanyFromSimappeCompanies(
  me: unknown,
  companiesRaw: unknown[],
): void {
  const companies = filterDatavaultCompanies(companiesRaw);
  if (!companies.length) return;

  const targetCompanyId = readAuthMeCompanyId(me);
  let match: Record<string, unknown> | null = null;

  if (targetCompanyId) {
    match =
      companies.find((row) => String(row['id'] ?? '').trim() === targetCompanyId) ?? null;
  }
  if (!match && companies.length === 1) {
    match = companies[0];
  }
  if (!match) return;

  const companyId = String(match['id'] ?? targetCompanyId ?? '').trim();
  const companyName = String(match['name'] ?? match['empresa'] ?? '').trim();
  const tenantId = extractTenantIdFromCompanyRecord(match);

  persistVaultNebulaCompanyContext({
    companyId,
    companyName,
    ...(tenantId ? { tenantId } : {}),
  });
}

export function unwrapSimappeCompanyList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];
  const record = raw as Record<string, unknown>;
  const response = record['response'];
  if (response && typeof response === 'object') {
    const content = (response as Record<string, unknown>)['content'];
    if (Array.isArray(content)) return content;
  }
  for (const key of ['content', 'data', 'items', 'results'] as const) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}
